import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Zap, ShieldCheck, Clock, ArrowRight } from "lucide-react";
import { GlowButton } from "@/components/GlowButton";
import { GlassCard } from "@/components/GlassCard";
import { HudDashboard } from "@/components/HudDashboard";
import { SectionHeader } from "@/components/SectionHeader";
import { CountUpStat } from "@/components/CountUpStat";
import { useSeo } from "@/hooks/useSeo";
import { STATS, SERVICES, SOLUTIONS } from "@/lib/data";

const fadeUp = {
  hidden: { opacity: 0, y: 26, filter: "blur(6px)" },
  show: (i = 0) => ({ opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};
const container = "mx-auto max-w-6xl px-4 sm:px-6 lg:px-8";
const VP_40 = { once: true, amount: 0.4 };
const VP_30 = { once: true, amount: 0.3 };

export default function Home() {
  useSeo({
    title: "Métis Tech Labs — AI-Powered Software, Automation & Systems",
    description:
      "Métis Tech Labs engineers AI-powered websites, automations, dashboards, and custom business systems that transform how enterprises operate.",
  });

  const trust = [
    { icon: Zap, text: "60+ systems shipped" },
    { icon: ShieldCheck, text: "99.9% uptime targets" },
    { icon: Clock, text: "Weeks → days automation" },
  ];

  const featured = SOLUTIONS.filter((s) => s.featured).slice(0, 3);

  return (
    <div>
      {/* HERO */}
      <section id="home" className="relative flex min-h-[100svh] items-center pt-28 pb-16">
        <div className={`${container} grid items-center gap-10 lg:grid-cols-12`}>
          <div className="lg:col-span-6">
            <motion.span variants={fadeUp} initial="hidden" animate="show" className="inline-flex items-center gap-2 rounded-full glass px-3 py-1.5 text-xs uppercase tracking-[0.16em] text-muted-brand">
              <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--neon-g)] shadow-[0_0_10px_var(--neon-g)]" />
              AI Systems • Automation • Dashboards
            </motion.span>

            <motion.h1 variants={fadeUp} custom={1} initial="hidden" animate="show" className="mt-6 font-display text-4xl font-semibold leading-[1.05] tracking-[-0.02em] sm:text-5xl lg:text-6xl">
              Build Intelligent Software.
              <br />
              <span className="text-gradient">Designed for Tomorrow.</span>
            </motion.h1>

            <motion.p variants={fadeUp} custom={2} initial="hidden" animate="show" className="mt-6 max-w-xl text-base leading-relaxed text-muted-brand sm:text-lg">
              We engineer AI-powered websites, automations, and custom business systems that transform how companies operate.
            </motion.p>

            <motion.div variants={fadeUp} custom={3} initial="hidden" animate="show" className="mt-8 flex flex-wrap items-center gap-4">
              <GlowButton as={Link} to="/contact" pulse data-testid="hero-book-consultation-button">
                Book Consultation <ArrowUpRight size={16} />
              </GlowButton>
              <GlowButton as={Link} to="/solutions" variant="secondary" data-testid="hero-view-solutions-button">
                View Solutions <ArrowRight size={16} />
              </GlowButton>
            </motion.div>

            <motion.div variants={fadeUp} custom={4} initial="hidden" animate="show" className="mt-10 flex flex-wrap gap-3">
              {trust.map((t) => (
                <div key={t.text} className="flex items-center gap-2 rounded-full glass px-3 py-1.5 text-xs text-muted-brand">
                  <t.icon size={14} className="text-neon-g" />
                  {t.text}
                </div>
              ))}
            </motion.div>
          </div>

          <div className="lg:col-span-6">
            <HudDashboard />
          </div>
        </div>
      </section>

      {/* STATS BAND */}
      <section className="relative py-10">
        <div className={container}>
          <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
            {STATS.map((s, i) => (
              <motion.div key={s.label} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={VP_40}>
                <GlassCard glow={i % 2 ? "purple" : "green"} className="p-6">
                  <div className="font-display text-3xl font-semibold text-white sm:text-4xl">
                    <CountUpStat value={s.value} suffix={s.suffix} />
                  </div>
                  <div className="mt-2 text-sm text-muted-brand">{s.label}</div>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CAPABILITIES PREVIEW */}
      <section className="relative py-20">
        <div className={container}>
          <SectionHeader eyebrow="What we build" title="Engineered capabilities, end to end" subtitle="From autonomous AI agents to enterprise dashboards — shipped, integrated, and measured." />
          <div className="mt-12 grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
            {SERVICES.slice(0, 6).map((s, i) => (
              <motion.div key={s.id} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={VP_30}>
                <GlassCard className="h-full p-6" glow={i % 2 ? "purple" : "green"}>
                  <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl glass text-neon-g">
                    <s.icon size={20} />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-white">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-brand">{s.desc}</p>
                </GlassCard>
              </motion.div>
            ))}
          </div>
          <div className="mt-10 flex justify-center">
            <GlowButton as={Link} to="/services" variant="ghost" className="px-5 py-2.5">
              Explore all services <ArrowRight size={15} />
            </GlowButton>
          </div>
        </div>
      </section>

      {/* FEATURED SOLUTIONS */}
      <section className="relative py-20">
        <div className={container}>
          <SectionHeader eyebrow="Selected work" title="Solutions from the lab" subtitle="Illustrative systems we design for enterprise teams." />
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            {featured.map((p, i) => (
              <motion.div key={p.id} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={VP_30}>
                <GlassCard glow={i % 2 ? "purple" : "green"} className="h-full overflow-hidden">
                  <div className="relative h-40 overflow-hidden">
                    <img src={p.image} alt={p.title} loading="lazy" className="h-full w-full object-cover opacity-80" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[rgba(11,11,13,0.9)] to-transparent" />
                    <span className="absolute left-3 top-3 rounded-full glass px-2.5 py-1 text-[10px] uppercase tracking-wider text-neon-g">{p.category}</span>
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-base font-semibold text-white">{p.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-brand">{p.summary}</p>
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </div>
          <div className="mt-10 flex justify-center">
            <GlowButton as={Link} to="/solutions" variant="ghost" className="px-5 py-2.5">
              View all solutions <ArrowRight size={15} />
            </GlowButton>
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="relative py-16">
        <div className={container}>
          <div className="neon-frame relative overflow-hidden rounded-3xl glass-strong px-6 py-14 text-center sm:px-12">
            <div className="pointer-events-none absolute inset-0 opacity-60" style={{ background: "radial-gradient(600px 240px at 50% 0%, rgba(57,255,20,0.10), transparent 70%)" }} />
            <h2 className="relative font-display text-3xl font-semibold tracking-tight sm:text-4xl">
              Ready to build your intelligent system?
            </h2>
            <p className="relative mx-auto mt-4 max-w-xl text-muted-brand">
              Book a consultation and we’ll map the fastest path from idea to deployed, measurable software.
            </p>
            <div className="relative mt-8 flex justify-center">
              <GlowButton as={Link} to="/contact" pulse data-testid="home-cta-book-button">
                Book Consultation <ArrowUpRight size={16} />
              </GlowButton>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
